import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface RestartStatusSummaryProps {
  summary?: {
    chipColor: 'success' | 'error' | 'warning' | 'default'
    statusLabel: string
    method: string
    completedAt: string | null
    reason?: string
  } | null
  isLoading?: boolean
  error?: Error | null
}

export function RestartStatusSummary({ summary, isLoading, error }: Readonly<RestartStatusSummaryProps>) {
  const { t } = useTranslation()

  const renderContent = () => {
    if (isLoading) {
      return <CircularProgress size={24} title="Loading last restart summary" />
    }
    if (error) {
      return (
        <Typography color="text.secondary" variant="caption">
          {t('controllers.restart_status.unavailable')}
        </Typography>
      )
    }
    if (summary) {
      const chipLabel = summary.method ? `${summary.statusLabel} (${summary.method})` : summary.statusLabel

      return (
        <Stack spacing={0.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip color={summary.chipColor} label={chipLabel} size="small" />
            {summary.completedAt && (
              <Typography color="text.secondary" variant="caption">
                at {summary.completedAt}
              </Typography>
            )}
          </Box>
          {summary.reason && (
            <Alert severity="error" sx={{ py: 0.5, mt: 0.5 }}>
              <Typography variant="caption">{summary.reason}</Typography>
            </Alert>
          )}
        </Stack>
      )
    }
    return (
      <Typography color="text.secondary" variant="caption">
        {t('controllers.restart_status.no_restarts')}
      </Typography>
    )
  }

  return renderContent()
}
